import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import config from '../config/config';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import './Login.css';

/**
 * Login Page Component
 * 
 * Provides authentication interface with:
 * - Cognito-native username/password login
 * - SSO-ready architecture (button prepared for future SSO)
 * - Form validation
 * - Error handling
 * - Loading states
 */
export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, loginWithSSO, completeNewPassword, forgotPassword, confirmForgotPassword, loading, pendingChallenge } = useAuth();
  const ssoAttempted = useRef(false);
  
  const showAdminLogin = searchParams.get('admin') === 'true';
  const ssoEnabled = config.sso?.enabled;

  // Track signed-out state in sessionStorage so it survives component remounts
  // and Amplify's internal OAuth processing which can cause re-renders.
  // The URL param sets it, and we clear it only when the user explicitly clicks sign-in.
  const justSignedOut = searchParams.get('signed-out') === 'true' || sessionStorage.getItem('justSignedOut') === 'true';
  
  useEffect(() => {
    if (searchParams.get('signed-out') === 'true') {
      sessionStorage.setItem('justSignedOut', 'true');
    }
  }, [searchParams]);

  // Auto-redirect to SSO if enabled and not using admin fallback or just signed out
  // Uses a ref to prevent infinite retry loops when SSO redirect fails
  useEffect(() => {
    if (ssoEnabled && !showAdminLogin && !justSignedOut && !loading && !ssoAttempted.current) {
      ssoAttempted.current = true;
      loginWithSSO().catch(() => {
        // If SSO redirect fails, stay on login page — don't retry
      });
    }
  }, [ssoEnabled, showAdminLogin, justSignedOut, loading, loginWithSSO]);
  
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  
  const [newPasswordData, setNewPasswordData] = useState({
    newPassword: '',
    confirmPassword: ''
  });

  // Forgot password state
  const [forgotPasswordStep, setForgotPasswordStep] = useState(null); // null | 'request' | 'confirm' | 'success'
  const [forgotEmail, setForgotEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [resetPasswordData, setResetPasswordData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  
  const [errors, setErrors] = useState({});
  const [loginError, setLoginError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Use challenge state from AuthContext (survives remounts)
  const showNewPasswordForm = pendingChallenge?.type === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED';

  /**
   * Handle input changes
   */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    
    // Clear login error
    if (loginError) {
      setLoginError('');
    }
  };

  /**
   * Handle new password input changes
   */
  const handleNewPasswordChange = (e) => {
    const { name, value } = e.target;
    setNewPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    
    // Clear login error
    if (loginError) {
      setLoginError('');
    }
  };

  /**
   * Validate form
   */
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handle form submission (Cognito-native login)
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    setLoginError('');
    
    try {
      const result = await login(formData.email, formData.password);
      
      if (result.success) {
        // Redirect to dashboard on successful login
        setIsSubmitting(false);
        navigate('/dashboard');
      } else if (result.challengeName === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        // Challenge is now stored in AuthContext, component will re-render with showNewPasswordForm = true
        setIsSubmitting(false);
        setLoginError('');
      } else if (result.nextStep) {
        // Handle other challenges (MFA, etc.)
        setIsSubmitting(false);
        setLoginError(result.message || 'Additional authentication required');
      } else {
        setIsSubmitting(false);
        setLoginError(result.message || 'Login failed. Please try again.');
      }
    } catch (error) {
      console.error('Login error:', error);
      
      // User-friendly error messages
      let errorMessage = 'An error occurred. Please try again.';
      
      if (error.name === 'NotAuthorizedException') {
        errorMessage = 'Incorrect email or password. Please check your credentials and try again.';
      } else if (error.name === 'UserNotFoundException') {
        errorMessage = 'User not found. Please check your email address.';
      } else if (error.name === 'UserNotConfirmedException') {
        errorMessage = 'Please verify your email address before logging in.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setLoginError(errorMessage);
      setIsSubmitting(false);
    }
  };

  /**
   * Handle SSO login (Future implementation)
   */
  const handleSSOLogin = async () => {
    try {
      sessionStorage.removeItem('justSignedOut');
      await loginWithSSO();
    } catch (error) {
      setLoginError('SSO login failed: ' + (error?.message || 'Unknown error'));
    }
  };

  /**
   * Validate new password form
   */
  const validateNewPasswordForm = () => {
    const newErrors = {};
    
    if (!newPasswordData.newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (newPasswordData.newPassword.length < 12) {
      newErrors.newPassword = 'Password must be at least 12 characters';
    }
    
    if (!newPasswordData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (newPasswordData.newPassword !== newPasswordData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handle new password submission
   */
  const handleNewPasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateNewPasswordForm()) {
      return;
    }
    
    setIsSubmitting(true);
    setLoginError('');
    
    try {
      const result = await completeNewPassword(newPasswordData.newPassword);
      
      if (result.success) {
        // Redirect to dashboard on successful password change
        navigate('/dashboard');
      } else {
        setLoginError(result.message || 'Password change failed. Please try again.');
      }
    } catch (error) {
      console.error('Password change error:', error);
      setLoginError(error.message || 'An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Handle forgot password — request code
   */
  const handleForgotPasswordRequest = async (e) => {
    e.preventDefault();
    const email = forgotEmail.trim();
    if (!email) {
      setErrors({ forgotEmail: 'Email is required' });
      return;
    }
    setIsSubmitting(true);
    setLoginError('');
    try {
      await forgotPassword(email);
      setForgotPasswordStep('confirm');
    } catch (error) {
      if (error.name === 'UserNotFoundException') {
        setLoginError('No account found with that email address.');
      } else if (error.name === 'LimitExceededException') {
        setLoginError('Too many attempts. Please try again later.');
      } else {
        setLoginError(error.message || 'Failed to send reset code. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Handle forgot password — confirm code + new password
   */
  const handleForgotPasswordConfirm = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!resetCode.trim()) newErrors.resetCode = 'Verification code is required';
    if (!resetPasswordData.newPassword) {
      newErrors.resetNewPassword = 'New password is required';
    } else if (resetPasswordData.newPassword.length < 12) {
      newErrors.resetNewPassword = 'Password must be at least 12 characters';
    }
    if (!resetPasswordData.confirmPassword) {
      newErrors.resetConfirmPassword = 'Please confirm your password';
    } else if (resetPasswordData.newPassword !== resetPasswordData.confirmPassword) {
      newErrors.resetConfirmPassword = 'Passwords do not match';
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setIsSubmitting(true);
    setLoginError('');
    try {
      await confirmForgotPassword(forgotEmail.trim(), resetCode.trim(), resetPasswordData.newPassword);
      setForgotPasswordStep('success');
    } catch (error) {
      if (error.name === 'CodeMismatchException') {
        setLoginError('Invalid verification code. Please check and try again.');
      } else if (error.name === 'ExpiredCodeException') {
        setLoginError('Verification code has expired. Please request a new one.');
      } else {
        setLoginError(error.message || 'Failed to reset password. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Reset forgot password flow back to login
   */
  const backToLogin = () => {
    setForgotPasswordStep(null);
    setForgotEmail('');
    setResetCode('');
    setResetPasswordData({ newPassword: '', confirmPassword: '' });
    setLoginError('');
    setErrors({});
  };

  return (
    <div className="login-page">
      <div className="login-background">
        <div className="login-gradient"></div>
      </div>
      
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <h1 className="login-title">TUI ReportHub</h1>
            <p className="login-subtitle">
              {ssoEnabled && !showAdminLogin ? (justSignedOut ? 'You have been signed out' : 'Redirecting to TUI SSO...') :
               forgotPasswordStep === 'request' ? 'Enter your email to receive a reset code' :
               forgotPasswordStep === 'confirm' ? 'Enter the code sent to your email' :
               forgotPasswordStep === 'success' ? 'Your password has been reset' :
               showNewPasswordForm ? 'Set your new password' : 'Sign in to manage your reports'}
            </p>
          </div>

          {ssoEnabled && !showAdminLogin ? (
            // SSO mode — show signed-out message or redirecting spinner
            <div className="login-form" style={{ textAlign: 'center', padding: '2rem 0' }}>
              {justSignedOut ? (
                <>
                  <p style={{ color: 'rgba(255, 255, 255, 0.7)', marginBottom: '1.5rem' }}>
                    You have been signed out.
                  </p>
                  <Button
                    variant="primary"
                    size="lg"
                    fullWidth
                    onClick={handleSSOLogin}
                    disabled={isSubmitting || loading}
                  >
                    Sign in again
                  </Button>
                </>
              ) : (
                <>
                  <div className="loading-spinner" style={{ margin: '0 auto 1rem' }}></div>
                  {loginError && (
                    <div className="login-error" role="alert" style={{ marginBottom: '1rem' }}>
                      <svg className="login-error-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {loginError}
                    </div>
                  )}
                  <Button
                    variant="outline"
                    size="lg"
                    fullWidth
                    onClick={handleSSOLogin}
                    disabled={isSubmitting || loading}
                  >
                    Sign in with TUI SSO
                  </Button>
                </>
              )}
              <div className="login-footer">
                <p className="login-footer-text">
                  Need help? Contact your administrator
                </p>
              </div>
            </div>
          ) : (
          <>
          {forgotPasswordStep === 'request' ? (
            // Forgot Password — Request Code
            <form onSubmit={handleForgotPasswordRequest} className="login-form" noValidate>
              {loginError && (
                <div className="login-error" role="alert">
                  <svg className="login-error-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {loginError}
                </div>
              )}
              <Input
                label="Email"
                type="email"
                name="forgotEmail"
                value={forgotEmail}
                onChange={(e) => { setForgotEmail(e.target.value); setErrors({}); setLoginError(''); }}
                placeholder="your.email@example.com"
                error={errors.forgotEmail}
                disabled={isSubmitting}
                required
                autoComplete="email"
              />
              <Button type="submit" variant="primary" size="lg" fullWidth loading={isSubmitting} disabled={isSubmitting}>
                Send Reset Code
              </Button>
              <button type="button" className="login-link-button" onClick={backToLogin}>
                Back to sign in
              </button>
            </form>
          ) : forgotPasswordStep === 'confirm' ? (
            // Forgot Password — Enter Code + New Password
            <form onSubmit={handleForgotPasswordConfirm} className="login-form" noValidate>
              {loginError && (
                <div className="login-error" role="alert">
                  <svg className="login-error-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {loginError}
                </div>
              )}
              <div className="login-info" style={{
                padding: 'var(--spacing-3)',
                background: 'rgba(59, 130, 246, 0.1)',
                border: '1px solid rgba(59, 130, 246, 0.3)',
                borderRadius: 'var(--radius-lg)',
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: 'var(--font-size-sm)',
                marginBottom: 'var(--spacing-2)'
              }}>
                A verification code has been sent to {forgotEmail}
              </div>
              <Input
                label="Verification Code"
                type="text"
                name="resetCode"
                value={resetCode}
                onChange={(e) => { setResetCode(e.target.value); setErrors({}); setLoginError(''); }}
                placeholder="Enter 6-digit code"
                error={errors.resetCode}
                disabled={isSubmitting}
                required
                autoComplete="one-time-code"
              />
              <Input
                label="New Password"
                type="password"
                name="resetNewPassword"
                value={resetPasswordData.newPassword}
                onChange={(e) => { setResetPasswordData(prev => ({ ...prev, newPassword: e.target.value })); setErrors({}); }}
                placeholder="Enter new password (min 12 characters)"
                error={errors.resetNewPassword}
                disabled={isSubmitting}
                required
                autoComplete="new-password"
              />
              <Input
                label="Confirm Password"
                type="password"
                name="resetConfirmPassword"
                value={resetPasswordData.confirmPassword}
                onChange={(e) => { setResetPasswordData(prev => ({ ...prev, confirmPassword: e.target.value })); setErrors({}); }}
                placeholder="Confirm new password"
                error={errors.resetConfirmPassword}
                disabled={isSubmitting}
                required
                autoComplete="new-password"
              />
              <Button type="submit" variant="primary" size="lg" fullWidth loading={isSubmitting} disabled={isSubmitting}>
                Reset Password
              </Button>
              <button type="button" className="login-link-button" onClick={backToLogin}>
                Back to sign in
              </button>
            </form>
          ) : forgotPasswordStep === 'success' ? (
            // Forgot Password — Success
            <div className="login-form">
              <div className="login-info" style={{
                padding: 'var(--spacing-4)',
                background: 'rgba(34, 197, 94, 0.1)',
                border: '1px solid rgba(34, 197, 94, 0.3)',
                borderRadius: 'var(--radius-lg)',
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: 'var(--font-size-sm)',
                textAlign: 'center'
              }}>
                Your password has been reset successfully. You can now sign in with your new password.
              </div>
              <Button variant="primary" size="lg" fullWidth onClick={backToLogin}>
                Back to Sign In
              </Button>
            </div>
          ) : !showNewPasswordForm ? (
            // Login Form
            <form onSubmit={handleSubmit} className="login-form" noValidate>
              {loginError && (
                <div className="login-error" role="alert">
                  <svg className="login-error-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {loginError}
                </div>
              )}
              
              <Input
                label="Email"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="your.email@example.com"
                error={errors.email}
                disabled={isSubmitting}
                required
                autoComplete="email"
              />
              
              <Input
                label="Password"
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Enter your password"
                error={errors.password}
                disabled={isSubmitting}
                required
                autoComplete="current-password"
              />
              
              <Button
                type="submit"
                variant="primary"
                size="lg"
                fullWidth
                loading={isSubmitting}
                disabled={isSubmitting || loading}
              >
                Sign In
              </Button>

              <button
                type="button"
                className="login-link-button"
                onClick={() => { setForgotPasswordStep('request'); setForgotEmail(formData.email); setLoginError(''); setErrors({}); }}
              >
                Forgot your password?
              </button>
            </form>
          ) : (
            // New Password Form
            <form onSubmit={handleNewPasswordSubmit} className="login-form" noValidate>
              {loginError && (
                <div className="login-error" role="alert">
                  <svg className="login-error-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {loginError}
                </div>
              )}
              
              <div className="login-info" style={{
                padding: 'var(--spacing-3)',
                background: 'rgba(59, 130, 246, 0.1)',
                border: '1px solid rgba(59, 130, 246, 0.3)',
                borderRadius: 'var(--radius-lg)',
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: 'var(--font-size-sm)',
                marginBottom: 'var(--spacing-4)'
              }}>
                Your temporary password has expired. Please set a new password (minimum 12 characters).
              </div>
              
              <Input
                label="New Password"
                type="password"
                name="newPassword"
                value={newPasswordData.newPassword}
                onChange={handleNewPasswordChange}
                placeholder="Enter new password (min 12 characters)"
                error={errors.newPassword}
                disabled={isSubmitting}
                required
                autoComplete="new-password"
              />
              
              <Input
                label="Confirm Password"
                type="password"
                name="confirmPassword"
                value={newPasswordData.confirmPassword}
                onChange={handleNewPasswordChange}
                placeholder="Confirm new password"
                error={errors.confirmPassword}
                disabled={isSubmitting}
                required
                autoComplete="new-password"
              />
              
              <Button
                type="submit"
                variant="primary"
                size="lg"
                fullWidth
                loading={isSubmitting}
                disabled={isSubmitting || loading}
              >
                Set New Password
              </Button>
            </form>
          )}
          
          {/* SSO Button — only show on admin fallback login */}
          {showAdminLogin && (
            <>
              <div className="login-divider">
                <span>or</span>
              </div>
              
              <Button
                variant="outline"
                size="lg"
                fullWidth
                onClick={handleSSOLogin}
                disabled={isSubmitting || loading}
              >
                <svg className="login-sso-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                </svg>
                Sign in with TUI SSO
              </Button>
            </>
          )}
          
          <div className="login-footer">
            <p className="login-footer-text">
              Need help? Contact your administrator
            </p>
          </div>
          </>
          )}
        </div>
      </div>
    </div>
  );
}
